import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth, useLanguage, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Building2, User, Lock, CheckCircle2, XCircle, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export const AcceptInvitationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { language } = useLanguage();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    password: "",
    confirmPassword: ""
  });

  const roleLabels = {
    worker: language === "sv" ? "Yrkesarbetare" : "Worker",
    supervisor: language === "sv" ? "Arbetsledare" : "Supervisor",
    site_manager: language === "sv" ? "Platschef" : "Site Manager",
    project_manager: language === "sv" ? "Projektledare" : "Project Manager",
    admin: "Admin"
  };

  useEffect(() => {
    if (token) {
      fetchInvitation();
    } else {
      setError(language === "sv" ? "Ingen inbjudningslänk hittades" : "No invitation link found");
      setLoading(false);
    }
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const res = await fetch(`${API}/invitations/by-token/${token}`);
      if (res.ok) {
        const data = await res.json();
        setInvitation(data);
      } else {
        const errorData = await res.json();
        setError(errorData.detail || (language === "sv" ? "Inbjudan hittades inte" : "Invitation not found"));
      }
    } catch (err) {
      setError(language === "sv" ? "Kunde inte hämta inbjudan" : "Failed to fetch invitation");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(language === "sv" ? "Lösenorden matchar inte" : "Passwords don't match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error(language === "sv" ? "Lösenordet måste vara minst 6 tecken" : "Password must be at least 6 characters");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/invitations/accept-with-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          name: formData.name,
          password: formData.password
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(language === "sv" ? "Välkommen! Ditt konto har skapats." : "Welcome! Your account has been created.");
        
        // Auto-login
        login(data.access_token, data.user);
        navigate("/dashboard");
      } else {
        const errorData = await res.json();
        toast.error(errorData.detail || (language === "sv" ? "Kunde inte acceptera inbjudan" : "Failed to accept invitation"));
      }
    } catch (err) {
      toast.error(language === "sv" ? "Ett fel uppstod" : "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <p className="text-slate-600">{language === "sv" ? "Laddar inbjudan..." : "Loading invitation..."}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              {language === "sv" ? "Inbjudan ogiltig" : "Invalid Invitation"}
            </h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => navigate("/login")} variant="outline">
              {language === "sv" ? "Gå till inloggning" : "Go to login"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Barlow Condensed' }}>
            {language === "sv" ? "Välkommen till Controlify" : "Welcome to Controlify"}
          </CardTitle>
          <CardDescription>
            {language === "sv" 
              ? "Du har blivit inbjuden att gå med i en organisation"
              : "You've been invited to join an organization"}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Invitation Info */}
          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{language === "sv" ? "Organisation" : "Organization"}</span>
              <span className="font-semibold text-slate-900">{invitation?.organization_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{language === "sv" ? "Din roll" : "Your role"}</span>
              <Badge className="bg-primary">{roleLabels[invitation?.role] || invitation?.role}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{language === "sv" ? "Inbjuden av" : "Invited by"}</span>
              <span className="text-slate-700">{invitation?.invited_by_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{language === "sv" ? "E-post" : "Email"}</span>
              <span className="text-slate-700">{invitation?.email}</span>
            </div>
          </div>

          {/* Create Account Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {language === "sv" ? "Ditt namn" : "Your name"}
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={language === "sv" ? "Förnamn Efternamn" : "First Last"}
                required
                data-testid="accept-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                {language === "sv" ? "Skapa lösenord" : "Create password"}
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="accept-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                {language === "sv" ? "Bekräfta lösenord" : "Confirm password"}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="accept-confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-12" 
              disabled={submitting}
              data-testid="accept-invitation-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {language === "sv" ? "Skapar konto..." : "Creating account..."}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {language === "sv" ? "Acceptera och skapa konto" : "Accept and create account"}
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-slate-500 text-center">
            {language === "sv" 
              ? "Genom att acceptera godkänner du våra användarvillkor."
              : "By accepting, you agree to our terms of service."}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
